import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { Button, Card } from '../ui';
import PasswordResetForm from './PasswordResetForm';
import { useAuthTracking } from '../../contexts/AnalyticsContext';

const PasswordResetPage: React.FC = () => {
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'form' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string>('');
  const { trackPasswordResetCompleted } = useAuthTracking();

  useEffect(() => {
    // Extract token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      setStatus('form');
    } else {
      setError('Invalid or missing reset token');
      setStatus('error');
    }
  }, []);

  const handleSuccess = () => {
    trackPasswordResetCompleted();
    setStatus('success');
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setStatus('error');
  };

  const handleBackToLogin = () => {
    // Navigate back to login
    window.location.href = '/';
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-avi-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Validating reset token...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="max-w-md w-full space-y-8"
        >
          <Card className="p-8 text-center">
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Password reset successful!
            </h2>
            <p className="text-gray-600 mb-6">
              Your password has been updated successfully. All your active sessions have been invalidated for security.
            </p>
            <Button onClick={handleBackToLogin} className="w-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Return to login
            </Button>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="max-w-md w-full space-y-8"
        >
          <Card className="p-8 text-center">
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100 mb-4">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Reset link invalid
            </h2>
            <p className="text-gray-600 mb-6">
              {error || 'This password reset link is invalid, expired, or has already been used.'}
            </p>
            <Button onClick={handleBackToLogin} variant="outline" className="w-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Return to login
            </Button>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <PasswordResetForm 
      token={token!} 
      onSuccess={handleSuccess}
      onError={handleError}
    />
  );
};

export default PasswordResetPage;